import { useState } from "react";
import { toast } from "sonner";
import { Receipt, DownloadSimple, Printer } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { api } from "../lib/api";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
}

const MONATE = ["Jan.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."];

function yearSummary(eintraege) {
  const m = new Map();
  for (const e of eintraege) {
    const y = e.ausgezahlt_am ? new Date(e.ausgezahlt_am).getFullYear() : "—";
    const cur = m.get(y) || { sum: 0, count: 0 };
    cur.sum += e.betrag || 0;
    cur.count += 1;
    m.set(y, cur);
  }
  return [...m.entries()]
    .sort((a, b) => String(b[0]).localeCompare(String(a[0])))
    .map(([year, v]) => ({ year, sum: v.sum, count: v.count }));
}

function toCsv(eintraege) {
  const head = ["Auszahlungsdatum", "Empfaenger", "Betrag (EUR)", "Anzahl Empfehlungen", "Empfehlungen"];
  const rows = eintraege.map((e) => [
    fmtDate(e.ausgezahlt_am),
    e.kunde_email || "",
    String(e.betrag),
    String(e.anzahl),
    (e.empfehlungen || []).join("; "),
  ]);
  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  return [head, ...rows].map((r) => r.map(esc).join(";")).join("\r\n");
}

export function JournalDialog({ advisorName, advisorEmail }) {
  const [data, setData] = useState({ eintraege: [], gesamt: 0, anzahl: 0 });
  const [jahr, setJahr] = useState("alle");
  const [monat, setMonat] = useState("alle");
  const load = async () => {
    try {
      const { data } = await api.get("/berater/journal");
      setData(data);
    } catch {
      /* ignore */
    }
  };

  const yearOf = (e) => (e.ausgezahlt_am ? String(new Date(e.ausgezahlt_am).getFullYear()) : "—");
  const monthOf = (e) => (e.ausgezahlt_am ? new Date(e.ausgezahlt_am).getMonth() + 1 : null);
  const quarterOf = (e) => { const m = monthOf(e); return m ? Math.ceil(m / 3) : null; };
  const years = [...new Set(data.eintraege.map(yearOf))].sort((a, b) => b.localeCompare(a));
  const yearEntries = jahr === "alle" ? data.eintraege : data.eintraege.filter((e) => yearOf(e) === jahr);
  const monthsAvail = jahr === "alle" ? [] : [...new Set(yearEntries.map(monthOf).filter(Boolean))].sort((a, b) => a - b);
  const quartersAvail = jahr === "alle" ? [] : [...new Set(yearEntries.map(quarterOf).filter(Boolean))].sort((a, b) => a - b);
  const isQuarter = typeof monat === "string" && monat.startsWith("Q");
  const entries =
    jahr === "alle" || monat === "alle"
      ? yearEntries
      : isQuarter
        ? yearEntries.filter((e) => quarterOf(e) === Number(monat.slice(1)))
        : yearEntries.filter((e) => String(monthOf(e)) === String(monat));
  const gesamt = entries.reduce((s, e) => s + (e.betrag || 0), 0);
  const anzahl = entries.length;
  const jahrLabel = jahr === "alle" ? "Alle Jahre" : monat === "alle" ? jahr : isQuarter ? `${monat} ${jahr}` : `${MONATE[Number(monat) - 1]} ${jahr}`;
  const currentYear = new Date().getFullYear();
  const prevYear = currentYear - 1;

  const exportCsv = () => {
    if (!entries.length) {
      toast.error("Keine Auszahlungen im gewählten Zeitraum.");
      return;
    }
    const csv = "\uFEFF" + toCsv(entries);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = jahr === "alle" ? "alle" : monat === "alle" ? jahr : isQuarter ? `${jahr}-${monat}` : `${jahr}-${String(monat).padStart(2, "0")}`;
    a.href = url;
    a.download = `auszahlungs-journal_${suffix}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("CSV exportiert.");
  };

  const printEntries = (list, label) => {
    if (!list.length) {
      toast.error(`Keine Auszahlungen: ${label}.`);
      return;
    }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const heute = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    const sum = list.reduce((s, e) => s + (e.betrag || 0), 0);
    const cnt = list.length;
    const ys = yearSummary(list);
    const yearRows = ys
      .map((y) => `<tr><td>${y.year}</td><td class="r">${y.count}</td><td class="r">${y.sum} €</td></tr>`)
      .join("");
    const rows = list
      .map(
        (e) =>
          `<tr><td>${fmtDate(e.ausgezahlt_am)}</td><td>${esc(e.kunde_email)}</td><td>${esc((e.empfehlungen || []).join(", "))}</td><td class="r">${e.betrag} €</td></tr>`
      )
      .join("");
    const html = `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>Auszahlungs-Journal</title>
      <style>
        *{font-family:Arial,Helvetica,sans-serif;color:#0A1128;}
        body{margin:32px;}
        h1{font-size:22px;margin:0 0 4px;}
        .sub{color:#666;font-size:13px;margin:0 0 20px;}
        .meta{font-size:13px;margin:0 0 18px;line-height:1.6;}
        table{width:100%;border-collapse:collapse;font-size:13px;}
        th,td{text-align:left;padding:8px 10px;border-bottom:1px solid #ddd;}
        th{background:#0A1128;color:#fff;font-size:12px;text-transform:uppercase;letter-spacing:.5px;}
        td.r,th.r{text-align:right;}
        tfoot td{font-weight:bold;font-size:15px;border-top:2px solid #0A1128;border-bottom:none;}
        .foot{margin-top:28px;font-size:11px;color:#888;}
        @media print{body{margin:12mm;} .noprint{display:none;}}
      </style></head><body>
      <h1>Auszahlungs-Journal</h1>
      <p class="sub">Ausgezahlte Prämien – Übersicht für das Finanzamt · Zeitraum: ${esc(label)}</p>
      <div class="meta">
        <div><strong>Berater:</strong> ${esc(advisorName || "")}</div>
        <div><strong>E-Mail:</strong> ${esc(advisorEmail || "")}</div>
        <div><strong>Erstellt am:</strong> ${heute}</div>
        <div><strong>Zeitraum:</strong> ${esc(label)}</div>
        <div><strong>Anzahl Auszahlungen:</strong> ${cnt}</div>
      </div>
      <h2 style="font-size:15px;margin:0 0 8px;">Zusammenfassung pro Jahr</h2>
      <table style="margin-bottom:24px;">
        <thead><tr><th>Jahr</th><th class="r">Auszahlungen</th><th class="r">Summe</th></tr></thead>
        <tbody>${yearRows}</tbody>
        <tfoot><tr><td>Gesamt</td><td class="r">${cnt}</td><td class="r">${sum} €</td></tr></tfoot>
      </table>
      <h2 style="font-size:15px;margin:0 0 8px;">Einzelne Auszahlungen</h2>
      <table>
        <thead><tr><th>Auszahlungsdatum</th><th>Empfänger</th><th>Empfehlungen</th><th class="r">Betrag</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td colspan="3">Gesamt ausgezahlt</td><td class="r">${sum} €</td></tr></tfoot>
      </table>
      <p class="foot">Erstellt mit Prämio. Alle Beträge in Euro.</p>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Bitte Pop-ups für den Druck erlauben.");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 350);
  };

  const printJournal = () => printEntries(entries, jahrLabel);

  const printVorjahr = () => {
    const list = data.eintraege.filter((e) => yearOf(e) === String(prevYear));
    printEntries(list, `${prevYear}`);
  };

  return (
    <Dialog onOpenChange={(o) => { if (o) { setJahr("alle"); setMonat("alle"); load(); } }}>
      <DialogTrigger asChild>
        <button data-testid="journal-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-[#0A1128]/15 bg-white text-sm font-semibold text-[#0A1128] transition-colors hover:bg-[#F1F5F9]">
          <Receipt size={18} weight="fill" color="#C5A059" /> Auszahlungs-Journal
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-2xl" data-testid="journal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">Auszahlungs-Journal</DialogTitle>
          <DialogDescription>Alle ausgezahlten Prämien – z. B. für das Finanzamt.</DialogDescription>
        </DialogHeader>

        {data.eintraege.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="journal-year-filter">
            <button
              data-testid="journal-year-alle"
              onClick={() => { setJahr("alle"); setMonat("alle"); }}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${jahr === "alle" ? "bg-[#0A1128] text-white" : "border border-[#E2E8F0] bg-white text-[#4A5568]"}`}
            >
              Alle
            </button>
            {years.map((y) => (
              <button
                key={y}
                data-testid={`journal-year-${y}`}
                onClick={() => { setJahr(y); setMonat("alle"); }}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${jahr === y ? "bg-[#0A1128] text-white" : "border border-[#E2E8F0] bg-white text-[#4A5568]"}`}
              >
                {y}
              </button>
            ))}
          </div>
        )}

        {jahr !== "alle" && monthsAvail.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="journal-month-filter">
            <button
              data-testid="journal-month-alle"
              onClick={() => setMonat("alle")}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${monat === "alle" ? "bg-[#C5A059] text-[#0A1128]" : "border border-[#E2E8F0] bg-white text-[#4A5568]"}`}
            >
              Alle Monate
            </button>
            {monthsAvail.map((m) => (
              <button
                key={m}
                data-testid={`journal-month-${m}`}
                onClick={() => setMonat(String(m))}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${String(monat) === String(m) ? "bg-[#C5A059] text-[#0A1128]" : "border border-[#E2E8F0] bg-white text-[#4A5568]"}`}
              >
                {MONATE[m - 1]}
              </button>
            ))}
          </div>
        )}

        {jahr !== "alle" && quartersAvail.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="journal-quarter-filter">
            {quartersAvail.map((q) => (
              <button
                key={q}
                data-testid={`journal-quarter-${q}`}
                onClick={() => setMonat(`Q${q}`)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${monat === `Q${q}` ? "bg-[#0A1128] text-white" : "border border-[#0A1128]/20 bg-white text-[#4A5568]"}`}
              >
                Q{q}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between rounded-xl border border-[#C5A059]/30 bg-[#FBF8F1] p-3.5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Ausgezahlt ({jahrLabel})</p>
            <p className="text-xs text-[#718096]">{anzahl} Auszahlung{anzahl === 1 ? "" : "en"}</p>
          </div>
          <p className="text-2xl font-extrabold text-[#0A1128]" data-testid="journal-gesamt">{gesamt} €</p>
        </div>

        {jahr === "alle" && data.eintraege.length > 0 && (
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-3" data-testid="journal-jahr-summary">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Zusammenfassung pro Jahr</p>
            <div className="space-y-1.5">
              {yearSummary(data.eintraege).map((y) => (
                <button key={y.year} onClick={() => setJahr(String(y.year))} className="flex w-full items-center justify-between text-sm" data-testid="journal-jahr-row">
                  <span className="text-[#4A5568]">{y.year} · {y.count} Auszahlung{y.count === 1 ? "" : "en"}</span>
                  <span className="font-bold text-[#0A1128]">{y.sum} €</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="max-h-[46vh] space-y-2 overflow-y-auto">
          {entries.length === 0 && (
            <p className="rounded-xl border border-dashed border-[#E2E8F0] px-4 py-8 text-center text-sm text-[#A0AEC0]">Keine Auszahlungen im gewählten Zeitraum.</p>
          )}
          {entries.map((e) => (
            <div key={e.id} className="rounded-xl border border-[#E2E8F0] bg-white p-3" data-testid="journal-eintrag">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#0A1128]">{e.kunde_email}</p>
                  <p className="mt-0.5 text-[11px] text-[#A0AEC0]">Ausgezahlt am {fmtDate(e.ausgezahlt_am)}</p>
                </div>
                <span className="shrink-0 rounded-full bg-[#276749]/10 px-2.5 py-0.5 text-sm font-bold text-[#276749]">{e.betrag} €</span>
              </div>
              {e.empfehlungen?.length > 0 && (
                <p className="mt-1.5 text-xs text-[#718096]">{e.anzahl} Empf.: {e.empfehlungen.join(", ")}</p>
              )}
            </div>
          ))}
        </div>

        {data.eintraege.some((e) => yearOf(e) === String(prevYear)) && (
          <button data-testid="journal-vorjahr-btn" onClick={printVorjahr} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#0A1128]/15 bg-white text-sm font-semibold text-[#0A1128] active:scale-[0.99]">
            <Printer size={16} weight="bold" color="#C5A059" /> Vorjahr {prevYear} als PDF
          </button>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button data-testid="journal-print-btn" onClick={printJournal} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-[#0A1128] text-sm font-semibold text-white active:scale-[0.99]">
            <Printer size={16} weight="bold" /> Drucken / PDF
          </button>
          <button data-testid="journal-export-btn" onClick={exportCsv} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-[#C5A059] text-sm font-semibold text-[#0A1128] active:scale-[0.99]">
            <DownloadSimple size={16} weight="bold" /> CSV
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
